import { memo, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { i18n } from '@/i18n';
import { codeLabel, isAnterior, type Surface } from '@/utils/dental';
import { emptyTooth, type SurfaceMark, type ToothState } from '@/utils/toothState';
import { ARCH_H, ARCH_W, archLayout, type Placement } from './archLayout';

export type Dentition = 'permanent' | 'primary';

type Props = {
  chart: ReadonlyMap<number, ToothState>;
  dentition: Dentition;
  selected: number | null;
  recent: ReadonlySet<number>;
  /** Teeth to mark steadily, e.g. what changed in the visit being viewed. */
  highlight?: ReadonlySet<number>;
  onSelect: (tooth: number) => void;
};

/** existing = blue, performed in the visit shown = green (pathology is red). */
function stateColor(performed: boolean): string {
  return performed ? 'var(--performed)' : 'var(--existing)';
}

function markColor(mark: SurfaceMark): string {
  switch (mark.kind) {
    case 'caries':
    case 'fracture':
    case 'wear':
      return 'var(--pathology)';
    default:
      return stateColor(mark.performed);
  }
}

function surfacesWith(s: ToothState, test: (m: SurfaceMark) => boolean): string {
  return Object.entries(s.surfaces)
    .filter(([, m]) => m && test(m))
    .map(([k]) => k)
    .join('');
}

const PRESENCE_KEY: Record<ToothState['presence'], string | null> = {
  present: null,
  missing: 'chart.odontogram.missing',
  implant: 'chart.odontogram.implant',
  pontic: 'chart.odontogram.pontic',
  remnant: 'chart.odontogram.remnant',
};

/** Plain yes/no conditions, in the order they are read out. */
const FLAGS: [(s: ToothState) => boolean, string][] = [
  [(s) => s.devital, 'chart.odontogram.devital'],
  [(s) => s.rootCanal, 'chart.odontogram.rootCanal'],
  [(s) => s.periapical, 'chart.odontogram.periapical'],
  [(s) => !!s.mobility, 'chart.odontogram.mobile'],
];

/** Spoken summary for screen readers. */
function describe(s: ToothState): string {
  const isCaries = (m: SurfaceMark) => m.kind === 'caries';
  const caries = surfacesWith(s, isCaries);
  const filled = surfacesWith(s, (m) => !isCaries(m));
  const presence = PRESENCE_KEY[s.presence];
  const crown = s.abutment ? 'chart.odontogram.abutment' : 'chart.odontogram.crown';
  const parts = [
    presence ? i18n.t(presence) : null,
    caries || s.whole?.kind === 'caries'
      ? i18n.t('chart.odontogram.caries', { surfaces: caries }).trim()
      : null,
    filled || (s.whole && !isCaries(s.whole))
      ? i18n.t('chart.odontogram.filling', { surfaces: filled }).trim()
      : null,
    s.crown && s.presence === 'present' ? i18n.t(crown) : null,
    ...FLAGS.filter(([on]) => on(s)).map(([, key]) => i18n.t(key)),
  ].filter(Boolean);
  return parts.length ? parts.join(', ') : i18n.t('chart.odontogram.empty');
}

type Badge = { label: string; color: string; title: string };

function badge(kind: 'mobility' | 'calculus' | 'impacted' | 'planned', color: string): Badge {
  return {
    label: i18n.t(`chart.odontogram.badgeLetter.${kind}`),
    color,
    title: i18n.t(`chart.odontogram.badge.${kind}`),
  };
}

function badgesFor(s: ToothState): Badge[] {
  const all: [boolean, Badge][] = [
    [!!s.mobility, badge('mobility', 'var(--pathology)')],
    [s.calculus, badge('calculus', 'var(--pathology)')],
    [s.impacted, badge('impacted', 'var(--n-70)')],
    [s.planned.length > 0, badge('planned', 'var(--info)')],
  ];
  return all.filter(([on]) => on).map(([, b]) => b);
}

// ── Tooth drawing, in the tooth's own frame ──────────────────
// x runs along the arch (+ = distal), y across it (+ = buccal / labial).
// The frame is mapped onto the arch with an SVG matrix built from the
// placement's tangent and normal.

function frame(p: Placement): string {
  return `matrix(${p.tx} ${p.ty} ${p.nx} ${p.ny} ${p.x} ${p.y})`;
}

function outlinePath(p: Placement): string {
  const w = p.w / 2;
  const d = p.d / 2;
  if (p.kind !== 'molar') {
    return `M ${-w} 0 A ${w} ${d} 0 1 0 ${w} 0 A ${w} ${d} 0 1 0 ${-w} 0 Z`;
  }
  const r = Math.min(w, d) * 0.6;
  return [
    `M ${-w + r} ${-d}`,
    `L ${w - r} ${-d}`,
    `Q ${w} ${-d} ${w} ${-d + r}`,
    `L ${w} ${d - r}`,
    `Q ${w} ${d} ${w - r} ${d}`,
    `L ${-w + r} ${d}`,
    `Q ${-w} ${d} ${-w} ${d - r}`,
    `L ${-w} ${-d + r}`,
    `Q ${-w} ${-d} ${-w + r} ${-d}`,
    'Z',
  ].join(' ');
}

/** Decorative grooves, as on a paper chart. */
function fissurePath(p: Placement): string | null {
  const { w, d } = p;
  switch (p.kind) {
    case 'molar':
      return [
        `M ${-0.3 * w} 0 L ${0.3 * w} 0`,
        `M ${-0.08 * w} 0 L ${-0.12 * w} ${0.34 * d}`,
        `M ${0.1 * w} 0 L ${0.14 * w} ${-0.34 * d}`,
      ].join(' ');
    case 'premolar':
      return `M ${-0.24 * w} ${0.04 * d} Q 0 ${-0.12 * d} ${0.24 * w} ${0.04 * d}`;
    case 'canine':
      return `M ${-0.14 * w} ${-0.06 * d} L ${0.14 * w} ${0.1 * d}`;
    default:
      return null;
  }
}

type Region = {
  surface: (p: Placement) => Surface;
  points: (w: number, d: number, ix: number, iy: number) => string;
};

const REGIONS: Region[] = [
  { surface: () => 'B', points: (w, d, ix, iy) => `${-w},${d} ${w},${d} ${ix},${iy} ${-ix},${iy}` },
  {
    surface: () => 'L',
    points: (w, d, ix, iy) => `${-w},${-d} ${w},${-d} ${ix},${-iy} ${-ix},${-iy}`,
  },
  { surface: () => 'D', points: (w, d, ix, iy) => `${w},${-d} ${w},${d} ${ix},${iy} ${ix},${-iy}` },
  {
    surface: () => 'M',
    points: (w, d, ix, iy) => `${-w},${-d} ${-w},${d} ${-ix},${iy} ${-ix},${-iy}`,
  },
  {
    surface: (p) => (isAnterior(p.tooth) ? 'I' : 'O'),
    points: (_w, _d, ix, iy) => `${-ix},${-iy} ${ix},${-iy} ${ix},${iy} ${-ix},${iy}`,
  },
];

function SurfaceFills({ p, s, clipId }: { p: Placement; s: ToothState; clipId: string }) {
  const anterior = isAnterior(p.tooth);
  const ix = p.w * (anterior ? 0.3 : 0.2);
  const iy = p.d * (anterior ? 0.1 : 0.2);
  return (
    <g clipPath={`url(#${clipId})`}>
      {REGIONS.map((r) => {
        const surface = r.surface(p);
        const mark = s.surfaces[surface] ?? (surface === 'O' || surface === 'I' ? s.whole : null);
        if (!mark) return null;
        return <polygon key={surface} points={r.points(p.w, p.d, ix, iy)} fill={markColor(mark)} />;
      })}
    </g>
  );
}

function Restorations({ p, s }: { p: Placement; s: ToothState }) {
  const { w, d } = p;
  return (
    <>
      {s.fracture && (
        <polyline
          points={`${-0.35 * w},${-0.3 * d} ${-0.05 * w},${0.1 * d} ${0.1 * w},${-0.05 * d} ${0.35 * w},${0.32 * d}`}
          fill="none"
          stroke="var(--n-100)"
          strokeWidth={1.8}
        />
      )}
      {s.veneer && (
        <line
          x1={-0.32 * w}
          x2={0.32 * w}
          y1={d / 2 - 1.5}
          y2={d / 2 - 1.5}
          stroke="var(--ceramic)"
          strokeWidth={3.5}
          strokeLinecap="round"
        />
      )}
      {s.presence === 'implant' && (
        <>
          <circle
            r={Math.min(w, d) * 0.4}
            fill="none"
            stroke={stateColor(s.presencePerformed)}
            strokeWidth={2}
          />
          <circle r={2.6} fill={stateColor(s.presencePerformed)} />
        </>
      )}
      {s.presence === 'remnant' && (
        // Only the root is left: its cross-section in the middle of the empty crown.
        <circle
          r={Math.min(w, d) * 0.24}
          fill="var(--root)"
          stroke="var(--tooth-line)"
          strokeWidth={1.4}
        />
      )}
      {s.rootCanal && (s.presence === 'present' || s.presence === 'remnant') && (
        <circle
          r={3}
          fill={stateColor(s.rootCanalPerformed)}
          stroke="var(--tooth)"
          strokeWidth={1.2}
        />
      )}
    </>
  );
}

function outlineStroke(
  s: ToothState,
  selected: boolean
): { color: string; width: number; dash?: string } {
  if (selected) return { color: 'var(--primary-40)', width: 2.5 };
  if (s.presence === 'missing') {
    // Extracted in this visit: green; long gone: grey.
    return s.presencePerformed
      ? { color: 'var(--performed)', width: 1.8, dash: '3 3' }
      : { color: 'var(--n-50)', width: 1.2, dash: '3 3' };
  }
  if (s.presence === 'remnant') return { color: 'var(--tooth-line)', width: 1.4, dash: '2 2' };
  if (s.crown) return { color: stateColor(s.crown.performed), width: 3 };
  if (s.impacted) return { color: 'var(--tooth-line)', width: 1.2, dash: '1.5 2.5' };
  return { color: 'var(--tooth-line)', width: 1.2 };
}

function bodyFill(s: ToothState, selected: boolean, hollow: boolean): string {
  if (hollow) return 'none';
  if (selected) return 'var(--primary-10)';
  // A non-vital tooth darkens; the chart shows it the same way.
  return s.devital ? 'var(--tooth-devital)' : 'var(--tooth)';
}

type ToothProps = {
  p: Placement;
  s: ToothState;
  selected: boolean;
  recent: boolean;
  highlighted: boolean;
  clipId: string;
  onSelect: (tooth: number) => void;
};

const Tooth = memo(function Tooth({
  p,
  s,
  selected,
  recent,
  highlighted,
  clipId,
  onSelect,
}: ToothProps) {
  const outline = outlinePath(p);
  const fissure = fissurePath(p);
  const absent = s.presence === 'missing';
  const hollow = absent || s.presence === 'remnant';
  const stroke = outlineStroke(s, selected);
  const labelDistance = p.d / 2 + 13;
  const badges = badgesFor(s).slice(0, 2);
  const classes = [
    'tooth',
    selected && 'is-selected',
    recent && 'is-recent',
    highlighted && 'is-highlighted',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    // biome-ignore lint/a11y/useSemanticElements: SVG group acting as a button
    <g
      className={classes}
      role="button"
      tabIndex={0}
      aria-label={`${s.tooth}: ${describe(s)}`}
      aria-pressed={selected}
      onClick={() => onSelect(s.tooth)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect(s.tooth);
        }
      }}
    >
      <circle className="tooth__hit" cx={p.x} cy={p.y} r={Math.max(p.w, p.d) / 2 + 9} />
      <g transform={frame(p)}>
        <clipPath id={clipId}>
          <path d={outline} />
        </clipPath>
        <path className="tooth__body" d={outline} fill={bodyFill(s, selected, hollow)} />
        {!hollow && <SurfaceFills p={p} s={s} clipId={clipId} />}
        {!(hollow || s.crown) && fissure && (
          <path
            d={fissure}
            fill="none"
            stroke="var(--tooth-line)"
            strokeWidth={1}
            strokeOpacity={0.8}
          />
        )}
        {!absent && <Restorations p={p} s={s} />}
        <path
          className="tooth__outline"
          d={outline}
          fill="none"
          stroke={stroke.color}
          strokeWidth={stroke.width}
          strokeDasharray={stroke.dash}
        />
      </g>
      <text
        className="tooth__num"
        x={p.x + p.nx * labelDistance}
        y={p.y + p.ny * labelDistance}
        textAnchor="middle"
        dominantBaseline="central"
      >
        {s.tooth}
      </text>
      {badges.map((b, k) => {
        const along = (k === 0 ? -1 : 1) * 9;
        const out = labelDistance + 15;
        return (
          <g
            key={b.label}
            transform={`translate(${p.x + p.nx * out + p.tx * along} ${p.y + p.ny * out + p.ty * along})`}
          >
            <title>{b.title}</title>
            <circle r={6.5} fill={b.color} />
            <text
              textAnchor="middle"
              dominantBaseline="central"
              fontSize={9}
              fontWeight={700}
              style={{ fill: '#fff' }}
            >
              {b.label}
            </text>
          </g>
        );
      })}
    </g>
  );
});

function inBridge(s: ToothState | undefined): boolean {
  return !!s && (s.abutment || s.presence === 'pontic');
}

type ArchProps = {
  placements: Placement[];
  chart: ReadonlyMap<number, ToothState>;
  selected: number | null;
  recent: ReadonlySet<number>;
  highlight: ReadonlySet<number>;
  idPrefix: string;
  onSelect: (tooth: number) => void;
};

function Arch({ placements, chart, selected, recent, highlight, idPrefix, onSelect }: ArchProps) {
  const states = placements.map((p) => chart.get(p.tooth) ?? emptyTooth(p.tooth));
  return (
    <g>
      {placements.map((p, i) => {
        const next = placements[i + 1];
        if (!(next && inBridge(states[i]) && inBridge(states[i + 1]))) return null;
        return (
          <line
            key={`bridge-${p.tooth}`}
            x1={p.x}
            y1={p.y}
            x2={next.x}
            y2={next.y}
            stroke={stateColor(!!states[i].crown?.performed)}
            strokeWidth={5}
            strokeLinecap="round"
          />
        );
      })}
      {placements.map((p, i) => (
        <Tooth
          key={p.tooth}
          p={p}
          s={states[i]}
          selected={selected === p.tooth}
          recent={recent.has(p.tooth)}
          highlighted={highlight.has(p.tooth)}
          clipId={`${idPrefix}-${p.tooth}`}
          onSelect={onSelect}
        />
      ))}
    </g>
  );
}

const LAYOUTS = { permanent: archLayout(false), primary: archLayout(true) };

/** Occlusal (top-down) chart of both arches. */
const NONE: ReadonlySet<number> = new Set();

export function Odontogram({
  chart,
  dentition,
  selected,
  recent,
  highlight = NONE,
  onSelect,
}: Props) {
  const { t } = useTranslation();
  const idPrefix = useId().replace(/:/g, '');
  const layout = LAYOUTS[dentition];
  return (
    // biome-ignore lint/a11y/useSemanticElements: an SVG drawing that groups tooth buttons
    <svg
      className="odontogram"
      viewBox={`0 0 ${ARCH_W} ${ARCH_H}`}
      role="group"
      aria-label={
        dentition === 'primary' ? t('chart.odontogram.primary') : t('chart.odontogram.permanent')
      }
    >
      <Arch
        placements={layout.upper}
        chart={chart}
        selected={selected}
        recent={recent}
        highlight={highlight}
        idPrefix={idPrefix}
        onSelect={onSelect}
      />
      <Arch
        placements={layout.lower}
        chart={chart}
        selected={selected}
        recent={recent}
        highlight={highlight}
        idPrefix={idPrefix}
        onSelect={onSelect}
      />
    </svg>
  );
}

/** The colour convention, then the symbols that are not colours. */
export function ChartLegend() {
  const { t } = useTranslation();
  const items: [string, string][] = [
    ['var(--existing)', t('chart.kind.existing')],
    ['var(--performed)', t('chart.kind.performed')],
    ['var(--pathology)', t('chart.kind.pathology')],
  ];
  return (
    <div className="legend">
      {items.map(([color, label]) => (
        <span key={label}>
          <i style={{ background: color }} />
          {label}
        </span>
      ))}
      <span>
        <i style={{ border: '1.5px dashed var(--n-50)', borderRadius: 6 }} />
        {codeLabel('missing')}
      </span>
      <span>
        <i style={{ background: 'var(--existing)', borderRadius: '50%', width: 8, height: 8 }} />
        {t('chart.legend.rootCanal')}
      </span>
      <span>
        <i style={{ border: '2px solid var(--existing)', borderRadius: '50%' }} />
        {codeLabel('implant')}
      </span>
      <span>
        <i
          style={{
            border: '1.5px dashed var(--tooth-line)',
            borderRadius: 6,
            background: 'radial-gradient(circle, var(--root) 35%, transparent 38%)',
          }}
        />
        {codeLabel('root_remnant')}
      </span>
      <span>
        <i style={{ background: 'var(--tooth-devital)', border: '1px solid var(--tooth-line)' }} />
        {codeLabel('devital')}
      </span>
    </div>
  );
}
